<template>
  <BaseSelectInput
    :id="id"
    v-model="model"
    :default-option-text="defaultOptionText"
    :helper-text-id="helperTextId"
    :label="label"
    :name="name"
    :required="required"
    :validation-rules="validationRules"
    inputClasses="pr-10"
  >
    <option
      v-for="state in states"
      :key="state.value"
      :value="state.value"
      v-text="state.label"
    />
    <template
      v-if="helperText"
      #helperText
    >
      <p v-text="helperText" />
    </template>
  </BaseSelectInput>
</template>

<script lang="ts" setup>
import { onBeforeMount, ref, watch } from 'vue'

import BaseSelectInput from '@js/components/global/forms/inputs/BaseSelectInput.vue'

import { log } from '@js/utils/logger'
import { getCountry } from '@js/api/address/getCountry'

import { CountryRegion } from '@js/api/types/address/CountryRegion'

defineOptions({
  name: 'BaseStateSelect',
})

const props = withDefaults(
  defineProps<{
    countryId: string
    id?: string
    name?: string
    label?: string
    required?: boolean
    helperText?: string
    helperTextId?: string
    validationRules?: string
    defaultOptionText?: string
  }>(),
  {
    id: 'state',
    name: 'state',
    helperText: '',
    required: true,
    helperTextId: '',
    label: 'State or Province',
    validationRules: 'required',
    defaultOptionText: 'Select a state or province',
  },
)

const emit = defineEmits<{
  clearState: [void]
  regionsChange: [regions: { label: string; value: string }[]]
}>()

const model = defineModel<string>({
  default: '',
})

const states = ref<{ label: string, value: string }[]>([])

const getStates = async () => {
  try {
    const data = await getCountry(props.countryId)

    if (data.available_regions !== null) {
      states.value = data.available_regions.map((state: CountryRegion) => ({
        label: state.name,
        value: state.code,
      }))
    } else {
      states.value = []
    }
    
    emit('regionsChange', states.value)
  } catch (error) {
    log({
      message: 'State Select Field Error',
      file: 'src/wcei/js/components/global/forms/BaseStateSelect.vue',
    })
  }
}

onBeforeMount(async (): Promise<void> => {
  await getStates()
})

watch(
  () => props.countryId,
  async () => {
    emit('clearState')

    await getStates()
  },
)
</script>
